import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Theme } from "@mui/material";
import { Box, styled, Typography, useMediaQuery } from "@mui/material";
import {
  Body as BaseBody,
  Header,
  NavigateBeforeButton,
  ContentContainer as BaseContentContainer,
} from "@components";
import type { PrescriptionDetail } from "@interfaces";
import { useTemplateEditContext } from "../../context/TemplateEditProvider";
import type { TemplateDetail } from "../../context/TemplateCreationContext";
import { SearchedMedicineCard } from "../MedicineSearch/components";
import { MedicineTreatmentForm } from "./components/MedicineTreatmentForm";
import { toPrefilledTreatmentAttributes } from "./utils/toPrefilledTreatmentAttributes";

const MedicineCardContainer = styled(Box)(({ theme }) => ({
  border: "1px solid #eaeaea",
  borderRadius: "8px",
  overflow: "hidden",
  width: "100%",
  [theme.breakpoints.up("md")]: {
    maxWidth: 500,
  },
  alignSelf: "flex-start",
}));

const ContentContainer = styled(BaseContentContainer)(({ theme }) => ({
  alignItems: "flex-start",
  [theme.breakpoints.up("md")]: {
    gap: "16px",
  },
}));

const HeaderBody = styled(BaseBody)(({ theme }) => ({
  alignItems: "center",
  flexDirection: "row",
  paddingBottom: 0,
  [theme.breakpoints.up("md")]: {
    padding: 24,
    paddingBottom: 0,
    maxWidth: "100%",
  },
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const Body = styled(BaseBody)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    gap: "16px",
    padding: 24,
    paddingTop: 0,
    paddingBottom: 0,
    maxWidth: 1200,
  },
  flex: 1,
}));

const DeleteButton = styled(Box)(({ theme }) => ({
  marginLeft: "auto",
  color: theme.palette.primary.main,
  cursor: "pointer",
}));

export function TemplateDetailEdit() {
  const navigate = useNavigate();
  const { requestId = "", templateId = "", localId = "" } = useParams();

  const { templateDetails, updateDetail, removeDetail } = useTemplateEditContext();

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const detail = useMemo(
    () => templateDetails.find((d) => d.localId === localId) ?? null,
    [templateDetails, localId],
  );

  const editRoute = `/app/solicitudes/${requestId}/plantilla/${templateId}/editar`;

  const handleBack = useCallback(() => {
    navigate(editRoute);
  }, [navigate, editRoute]);

  const onSubmit = useCallback(
    (formData: PrescriptionDetail) => {
      if (!detail) return;
      updateDetail(localId, {
        ...detail,
        ...formData,
      } as Omit<TemplateDetail, "localId">);
      navigate(editRoute);
    },
    [detail, localId, updateDetail, navigate, editRoute],
  );

  const handleDelete = useCallback(() => {
    removeDetail(localId);
    navigate(editRoute);
  }, [removeDetail, localId, navigate, editRoute]);

  if (!detail) {
    navigate(editRoute);

    return null;
  }

  return (
    <>
      <Header
        title={!isDesktop ? "Editar Tratamiento" : undefined}
        useMekiDocIcon={isDesktop}
        titleColor="black"
        left={<NavigateBeforeButton onClick={handleBack} />}
      />

      <ContentContainer disableGutters>
        <HeaderBody>
          <Typography variant="h4" fontWeight={"medium"}>
            Editar Tratamiento
          </Typography>
          <DeleteButton onClick={handleDelete}>
            <Typography variant="body1" fontWeight={500}>
              Eliminar
            </Typography>
          </DeleteButton>
        </HeaderBody>
        <Body>
          {detail.medicine && (
            <MedicineCardContainer>
              <SearchedMedicineCard medicine={detail.medicine} isTreatmentCard={true} />
            </MedicineCardContainer>
          )}
          <MedicineTreatmentForm
            prefilledMedicineAttributes={toPrefilledTreatmentAttributes(detail)}
            formManagement={{
              onSubmit,
              isLoading: false,
              handleDelete,
            }}
            isEditing
          />
        </Body>
      </ContentContainer>
    </>
  );
}
